import {
  $chatChannel, $chatDraft, $chatLines, $chatOpen, channelLabel, chatChoices, loadChannel, nextChoice, noteSent, pickChannel, saveChannel,
  type ChatLine,
} from "../state/chat";
import { safeStorage } from "../state/estateBanner";
import { $connection, $plots, $presence, $session, toast } from "../state/stores";

export const VISIBLE_LINES = 8;
export const FADE_MS = 8000;

export interface ChatDeps {
  send(channel: string, body: string): void;
  leave(): void;
  setBlocked(blocked: boolean): void;
  now?: () => number;
}

export interface ChatLayer { open(): void; close(): void }

/** The chat layer bottom-left (spec 2i §3.1–3.2). Every string goes in through textContent. */
export function mountChat(root: HTMLElement, deps: ChatDeps): ChatLayer {
  const now = deps.now ?? Date.now;
  const box = document.createElement("div");
  box.className = "chat";
  box.innerHTML = `<div class="chat-log"></div><div class="chat-input"><span class="chat-chan"></span><input maxlength="500" autocomplete="off" spellcheck="false" enterkeyhint="send"><button class="chat-touch chat-next" type="button">頻道</button><button class="chat-touch chat-x" type="button" aria-label="關閉">✕</button></div>`;
  root.appendChild(box);
  const log = box.querySelector<HTMLElement>(".chat-log")!;
  const chan = box.querySelector<HTMLElement>(".chat-chan")!;
  const input = box.querySelector<HTMLInputElement>("input")!;

  const choices = () => chatChoices($presence.get(), $plots.get(), $session.get()?.plots ?? []);
  const span = (cls: string, text: string) => {
    const s = document.createElement("span");
    s.className = cls;
    s.textContent = text;
    return s;
  };
  const lineEl = (l: ChatLine) => {
    const el = document.createElement("div");
    el.className = `chat-line chan-${l.kind}${l.mention ? " mention" : ""}`;
    el.append(span("chat-tag", `[${channelLabel(l.channel, $plots.get())}] `), span("chat-name", `${l.senderName}：`), span("chat-body", l.body));
    return el;
  };
  let wasOpen = false;
  const render = () => {
    const open = $chatOpen.get(), t = now();
    const all = $chatLines.get();
    const lines = open ? all : all.slice(-VISIBLE_LINES).filter((l) => t - l.receivedAt < FADE_MS);
    // Open, a reader who scrolled back stays put; closed, the log clips at its top so the newest
    // line has to be scrolled into view.
    const follow = !open || !wasOpen || log.scrollHeight - log.scrollTop - log.clientHeight < 4;
    log.replaceChildren(...lines.map(lineEl));
    box.classList.toggle("open", open);
    if (follow) log.scrollTop = log.scrollHeight;
    wasOpen = open;
  };
  const renderChannel = () => {
    const c = choices().find((x) => x.id === $chatChannel.get());
    chan.textContent = `[${c?.label ?? "附近"}]`;
  };

  function open(): void {
    if ($chatOpen.get()) return;
    const draft = $chatDraft.get();
    $chatDraft.set(null);
    const list = choices();
    $chatChannel.set(pickChannel(draft?.channel ?? loadChannel(safeStorage()), list));
    if (draft && !list.some((c) => c.id === draft.channel)) toast("原本的頻道已不能用，送出前請確認頻道");
    $chatOpen.set(true);
    deps.setBlocked(true);
    input.value = draft?.body ?? "";
    input.focus();
  }
  function close(): void {
    if (!$chatOpen.get()) return;
    $chatOpen.set(false);
    input.blur();
    deps.setBlocked(false);
  }

  input.addEventListener("keydown", (e) => {
    e.stopPropagation(); // the world's keys (Tab inventory, Esc menu, 1–9, E) stay out while typing
    // Keys that drive an input method (Enter confirms, Esc clears, Tab picks) belong to it; Safari
    // reports the confirming Enter with isComposing false but keyCode 229.
    if (e.isComposing || e.keyCode === 229) return;
    if (e.key === "Tab") { e.preventDefault(); $chatChannel.set(nextChoice(choices(), $chatChannel.get())); return; }
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (e.key !== "Enter") return;
    e.preventDefault();
    const body = input.value.trim();
    const channel = $chatChannel.get();
    if (body && body !== "/leave" && $connection.get() !== "online") { toast("離線中，訊息沒送出"); return; }
    if (body === "/leave") deps.leave();
    else if (body) { deps.send(channel, body); noteSent(channel, body); saveChannel(safeStorage(), channel); }
    close();
  });
  // Clicking the world takes the pointer lock and the focus: chat closes instead of swallowing keys,
  // and keeps what was typed for the next open.
  input.addEventListener("blur", () => {
    if (!$chatOpen.get()) return;
    if (input.value.trim()) $chatDraft.set({ channel: $chatChannel.get(), body: input.value });
    close();
  });
  // Touch: Tab and Esc as buttons (spec 2p §2.5). Preventing pointerdown keeps the input focused, so its
  // blur handler does not close the chat first (Review Focus 3).
  for (const b of box.querySelectorAll<HTMLButtonElement>(".chat-touch")) b.addEventListener("pointerdown", (e) => e.preventDefault());
  box.querySelector(".chat-next")!.addEventListener("click", () => { $chatChannel.set(nextChoice(choices(), $chatChannel.get())); input.focus(); });
  box.querySelector(".chat-x")!.addEventListener("click", () => close());
  // Under pointer lock wheel events land on the canvas, so the open history scrolls by hand.
  window.addEventListener("wheel", (e) => { if ($chatOpen.get()) log.scrollTop += e.deltaY; });

  $chatLines.subscribe(render);
  $plots.subscribe(render);
  $chatOpen.subscribe(() => { render(); renderChannel(); });
  $chatChannel.subscribe(renderChannel);
  setInterval(() => { if (!$chatOpen.get()) render(); }, 1000); // fade lines out
  return { open, close };
}
